import React, { useMemo, useState } from 'react';
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import LocalCafeOutlinedIcon from '@mui/icons-material/LocalCafeOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import Diversity3OutlinedIcon from '@mui/icons-material/Diversity3Outlined';
import FavoriteBorderOutlinedIcon from '@mui/icons-material/FavoriteBorderOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PRESERVE_LINE_BREAKS_SX } from '../eventFormConstants';
import { APP_EVENT_PRIMARY, buildEventLivePreviewModel } from '../eventLivePreviewModel';
import LivePreviewPhoneFrame from './LivePreviewPhoneFrame';

const TYPE_ICONS = {
  dinner: RestaurantOutlinedIcon,
  coffee: LocalCafeOutlinedIcon,
  networking: GroupsOutlinedIcon,
  wellness: SpaOutlinedIcon,
  workshop: SchoolOutlinedIcon,
  partner: HandshakeOutlinedIcon,
  community: Diversity3OutlinedIcon,
};

const PreviewSectionTitle = ({ children, sx }) => (
  <Typography
    sx={{
      color: '#222126',
      fontWeight: 600,
      fontSize: 14,
      lineHeight: '20px',
      mb: 1,
      mt: 2,
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const PreviewAvatar = ({ src, name, size = 40 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      overflow: 'hidden',
      flexShrink: 0,
      bgcolor: '#E6E4E8',
      border: '0.5px solid #E6E4E8',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6B6A6E',
      fontWeight: 700,
      fontSize: size * 0.38,
    }}
  >
    {src ? (
      <Box
        component="img"
        src={src}
        alt=""
        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    ) : (
      (name || 'G').charAt(0).toUpperCase()
    )}
  </Box>
);

const EventLivePreviewMenuModal = ({ open, onClose, menuSections }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '16px',
        maxHeight: '78vh',
      },
    }}
  >
    <Box sx={{ position: 'relative', pt: 1.75, px: 2, pb: 0.5 }}>
      <Typography sx={{ textAlign: 'center', fontWeight: 600, fontSize: 18, color: '#2D3748' }}>
        Set Menu
      </Typography>
      <IconButton
        aria-label="Close menu"
        onClick={onClose}
        size="small"
        sx={{ position: 'absolute', right: 8, top: 8, color: '#B3B2B6' }}
      >
        <CloseIcon />
      </IconButton>
      <Typography
        sx={{ textAlign: 'center', color: '#2D3748', fontSize: 14, lineHeight: 1.3, mb: 1 }}
      >
        Designed to keep the table relaxed
        <br />
        and uninterrupted
      </Typography>
    </Box>
    <DialogContent sx={{ pt: 0.5, px: 2, pb: 1.5 }}>
      {menuSections.map((section) => (
        <Box key={section.course} sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 13,
              color: '#2D3748',
              mb: 1,
              textTransform: 'capitalize',
            }}
          >
            {section.course}
          </Typography>
          {section.dishes.map((dish, index) => (
            <Box key={dish.id || `${section.course}-${index}`} sx={{ mb: 1 }}>
              {index > 0 ? (
                <Typography
                  sx={{ color: 'rgba(30,29,32,0.5)', fontSize: 13, fontStyle: 'italic', mb: 1 }}
                >
                  or
                </Typography>
              ) : null}
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                {dish.imagePreview ? (
                  <Box
                    component="img"
                    src={dish.imagePreview}
                    alt=""
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '4px',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '8px',
                      bgcolor: '#E6E4E8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: 8, fontWeight: 700, transform: 'rotate(-45deg)' }}>
                      NO IMAGE
                    </Typography>
                  </Box>
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{ fontWeight: 500, fontSize: 15, color: '#1E1D20', lineHeight: 1.4 }}
                  >
                    {dish.dishName || 'Untitled dish'}
                  </Typography>
                  {dish.description ? (
                    <Typography
                      sx={{
                        color: 'rgba(30,29,32,0.5)',
                        fontSize: 13,
                        lineHeight: 1.4,
                        mt: 0.25,
                        ...PRESERVE_LINE_BREAKS_SX,
                      }}
                    >
                      {dish.description}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pt: 1 }}>
        <InfoOutlinedIcon sx={{ fontSize: 17, color: '#D37A89' }} />
        <Typography sx={{ color: '#D37A89', fontSize: 14 }}>
          Vegetarian options available on request
        </Typography>
      </Box>
    </DialogContent>
  </Dialog>
);

const EventLivePreview = ({ form, eventData, partnerProfile, isPartnerEvent = false }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const preview = useMemo(
    () => buildEventLivePreviewModel({ form, eventData, partnerProfile, isPartnerEvent }),
    [form, eventData, partnerProfile, isPartnerEvent],
  );

  const TypeIcon = TYPE_ICONS[preview.typeKey] || RestaurantOutlinedIcon;
  const hasAttendanceRequirements = Boolean(
    preview.eligibilityBadge || preview.requireJoinApproval,
  );

  return (
    <>
      <LivePreviewPhoneFrame
        title="Live preview"
        subtitle="How this event will appears in the SureSpace app."
      >
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', bgcolor: '#FFFFFF' }}>
          <Box
            sx={{
              minHeight: 320,
              bgcolor: '#090B12',
              px: 2,
              pt: 3,
              pb: 2,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              position: 'relative',
            }}
          >
            {preview.coverImageUrl ? (
              <Box
                component="img"
                key={preview.coverImageUrl}
                src={preview.coverImageUrl}
                alt=""
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
              />
            ) : null}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(9,11,18,0) 0%, rgba(9,11,18,0.85) 100%)',
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                {preview.eligibilityBadge ? (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      borderRadius: 999,
                      border: '1px solid',
                      borderColor:
                        preview.eligibilityBadge.variant === 'male' ? '#BFD9FF' : '#E9C3E3',
                      bgcolor: preview.eligibilityBadge.variant === 'male' ? '#EEF6FF' : '#FFFFFF',
                      px: 0.75,
                      py: 0.4,
                    }}
                  >
                    <Typography
                      sx={{
                        color: preview.eligibilityBadge.variant === 'male' ? '#2E6FD8' : '#A33C92',
                        fontWeight: 600,
                        fontSize: 13,
                        mr: 0.75,
                      }}
                    >
                      {preview.eligibilityBadge.icon}
                    </Typography>
                    <Typography
                      sx={{
                        color: preview.eligibilityBadge.variant === 'male' ? '#2E6FD8' : '#A33C92',
                        fontWeight: 600,
                        fontSize: 12,
                      }}
                    >
                      {preview.eligibilityBadge.text}
                    </Typography>
                  </Box>
                ) : (
                  <Box />
                )}
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: 999,
                    border: '1px solid rgba(255,255,255,0.65)',
                    bgcolor: 'rgba(9,11,18,0.32)',
                    px: 1.25,
                    py: 0.5,
                  }}
                >
                  <PeopleAltOutlinedIcon sx={{ fontSize: 14, color: '#fff' }} />
                  <Typography sx={{ ml: 0.75, color: '#fff', fontWeight: 500, fontSize: 12 }}>
                    {preview.seatsRemainingLabel}
                  </Typography>
                </Box>
              </Box>

              <Typography
                sx={{
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 18,
                  lineHeight: '24px',
                  mb: 1,
                  opacity: preview.isTitlePlaceholder ? 0.55 : 1,
                }}
              >
                {preview.title}
              </Typography>
              {preview.wingmanDescription ? (
                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: 10,
                    lineHeight: '18px',
                    mb: 1.25,
                  }}
                >
                  {preview.wingmanDescription}
                </Typography>
              ) : null}
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    opacity: preview.isDatePlaceholder ? 0.7 : 1,
                  }}
                >
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: '#fff' }} />
                  <Typography sx={{ ml: 1, color: '#fff', fontWeight: 500, fontSize: 12 }}>
                    {preview.dateLabel}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    opacity: preview.isTimePlaceholder ? 0.7 : 1,
                  }}
                >
                  <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: '#fff' }} />
                  <Typography sx={{ ml: 1, color: '#fff', fontWeight: 500, fontSize: 12 }}>
                    {preview.timeLabel}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <Box sx={{ display: 'flex', borderBottom: '1px solid #E9E7EB', pb: 1.25, mb: 1.5 }}>
              {[
                {
                  key: 'type',
                  icon: TypeIcon,
                  value: '',
                  label: preview.typeLabel,
                  muted: preview.isTypePlaceholder,
                },
                { key: 'persons', value: preview.bookedCount, label: 'Persons' },
                { key: 'spots', value: String(preview.seatsRemaining), label: 'Spots Left' },
              ].map((stat, index, list) => {
                const Icon = stat.icon;
                return (
                  <Box
                    key={stat.key}
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      position: 'relative',
                      px: 0.5,
                    }}
                  >
                    {Icon ? (
                      <Icon sx={{ fontSize: 18, color: APP_EVENT_PRIMARY, mb: 0.25 }} />
                    ) : null}
                    {stat.value ? (
                      <Typography
                        sx={{
                          color: APP_EVENT_PRIMARY,
                          fontWeight: 600,
                          fontSize: 15,
                          lineHeight: '20px',
                        }}
                      >
                        {stat.value}
                      </Typography>
                    ) : null}
                    <Typography
                      sx={{
                        color: '#1E1D20',
                        fontWeight: 500,
                        fontSize: 12,
                        lineHeight: '16px',
                        opacity: stat.muted ? 0.5 : 1,
                      }}
                    >
                      {stat.label}
                    </Typography>
                    {index < list.length - 1 ? (
                      <Box
                        sx={{
                          position: 'absolute',
                          right: 0,
                          top: 8,
                          width: '1px',
                          height: 36,
                          bgcolor: '#E0DEE4',
                        }}
                      />
                    ) : null}
                  </Box>
                );
              })}
            </Box>

            {preview.showHost ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#FBC4AB33',
                  borderRadius: '12px',
                  p: 1.25,
                  mb: 1.25,
                }}
              >
                <PreviewAvatar src={preview.hostAvatarUrl} name={preview.hostName} size={41} />
                <Box sx={{ ml: 1, minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{ color: '#1E1D20', fontWeight: 600, fontSize: 11, lineHeight: '16px' }}
                  >
                    Hosted by
                  </Typography>
                  <Typography sx={{ color: '#1E1D20', fontSize: 14, lineHeight: '18px' }} noWrap>
                    {preview.hostName}
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {preview.description ? (
              <Box>
                <PreviewSectionTitle sx={{ mt: 1 }}>Description</PreviewSectionTitle>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: '#1A1614',
                    lineHeight: 1.5,
                    ...PRESERVE_LINE_BREAKS_SX,
                  }}
                >
                  {preview.description}
                </Typography>
              </Box>
            ) : null}

            {preview.requireJoinApproval ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  bgcolor: 'rgba(247, 148, 164, 0.15)',
                  borderRadius: '12px',
                  border: '1px solid #1E1D200D',
                  p: 1.25,
                  mt: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '18px',
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.25,
                    flexShrink: 0,
                  }}
                >
                  <FavoriteBorderOutlinedIcon sx={{ fontSize: 18, color: APP_EVENT_PRIMARY }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1E1D20', mb: 0.25 }}>
                    Host Approval Required
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: '#1E1D20', lineHeight: '18px' }}>
                    Your registration will be reviewed by the host before your seat is confirmed to
                    ensure a cohesive group dynamic.
                  </Typography>
                </Box>
              </Box>
            ) : null}

            {preview.topics.length > 0 ? (
              <>
                <PreviewSectionTitle>The Room’s Energy</PreviewSectionTitle>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {preview.topics.map((topic) => (
                    <Box
                      key={topic}
                      sx={{
                        bgcolor: 'rgba(255, 218, 185, 0.4)',
                        borderRadius: 999,
                        px: 1.5,
                        py: 0.75,
                      }}
                    >
                      <Typography sx={{ fontWeight: 500, fontSize: 12, color: '#1E1D20' }}>
                        {topic}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : null}

            {hasAttendanceRequirements ? (
              <>
                <PreviewSectionTitle>Attendance Requirements</PreviewSectionTitle>
                <Typography
                  sx={{ color: 'rgba(30,29,32,0.65)', fontSize: 12, lineHeight: '18px', mb: 1.25 }}
                >
                  This event has curated attendance requirements to ensure the right group dynamic
                  and conversation quality.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {preview.eligibilityBadge ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '16px',
                          bgcolor: 'rgba(247, 148, 164, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.25,
                        }}
                      >
                        <Typography
                          sx={{ color: APP_EVENT_PRIMARY, fontWeight: 700, fontSize: 14 }}
                        >
                          {preview.eligibilityBadge.icon}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 500, fontSize: 13, color: '#1E1D20' }}>
                        {preview.eligibilityBadge.variant === 'male'
                          ? 'Men Only Event'
                          : 'Women Only Event'}
                      </Typography>
                    </Box>
                  ) : null}
                  {preview.requireJoinApproval ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '16px',
                          bgcolor: 'rgba(247, 148, 164, 0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 1.25,
                        }}
                      >
                        <LockOutlinedIcon sx={{ fontSize: 16, color: APP_EVENT_PRIMARY }} />
                      </Box>
                      <Typography sx={{ fontWeight: 500, fontSize: 13, color: '#1E1D20' }}>
                        Host Approval Required
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              </>
            ) : null}

            {preview.menuCourseCount > 0 ? (
              <>
                <PreviewSectionTitle>The Experience</PreviewSectionTitle>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    bgcolor: 'rgba(247, 148, 164, 0.15)',
                    borderRadius: '12px',
                    border: '1px solid #1E1D200D',
                    p: 1.25,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '20px',
                      bgcolor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 1.25,
                      flexShrink: 0,
                    }}
                  >
                    <RestaurantOutlinedIcon sx={{ fontSize: 20, color: APP_EVENT_PRIMARY }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1E1D20' }}>
                      Curated {preview.menuCourseCount}-Course Menu
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#1E1D20' }}>
                      Designed to keep the table relaxed and uninterrupted.
                    </Typography>
                    <Typography
                      component="button"
                      onClick={() => setMenuOpen(true)}
                      sx={{
                        display: 'block',
                        ml: 'auto',
                        mt: 0.75,
                        border: 0,
                        bgcolor: 'transparent',
                        p: 0,
                        color: APP_EVENT_PRIMARY,
                        fontWeight: 700,
                        fontSize: 13,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      View More
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : null}

            <PreviewSectionTitle>Location</PreviewSectionTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '200px',
                  mr: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '0.5px solid #1E1D20',
                  opacity: 0.5,
                  flexShrink: 0,
                }}
              >
                <PlaceOutlinedIcon sx={{ fontSize: 22, color: APP_EVENT_PRIMARY }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1E1D20' }}>
                  {preview.isOnline
                    ? preview.eventFormatLabel
                    : preview.venueName || 'Venue to be set'}
                </Typography>
                <Typography sx={{ color: 'rgba(30,29,32,0.5)', fontSize: 11 }}>
                  {preview.isOnline
                    ? 'Link will be provided after confirming your booking'
                    : preview.location || 'Address to be set'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 1.25,
            borderTop: '1px solid #F3F1F5',
            bgcolor: '#fff',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 44,
              borderRadius: '200px',
              bgcolor: APP_EVENT_PRIMARY,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 1.5,
            }}
          >
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: 11,
                textAlign: 'center',
                lineHeight: 1.25,
              }}
            >
              {preview.joinLabel}
            </Typography>
          </Box>
        </Box>
      </LivePreviewPhoneFrame>

      <EventLivePreviewMenuModal
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        menuSections={preview.menuSections}
      />
    </>
  );
};

export default EventLivePreview;
